<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="현장 변경 신청"
        subtitle="새로운 현장으로 소속을 변경하세요"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 좌측: 현재 소속 & 안내 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 현재 소속 상태 -->
            <div v-if="currentAffiliation" class="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
              <div class="flex items-center justify-between mb-3">
                <span class="text-[12px] font-medium text-emerald-700">현재 소속 현장</span>
                <span v-if="!canChangeAffiliation" class="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-[11px] font-bold">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  {{ daysUntilChange }}일 후 변경
                </span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
                  </svg>
                </div>
                <div>
                  <p class="text-[14px] font-bold text-slate-900">{{ currentAffiliation.propertyName }}</p>
                  <p class="text-[12px] text-slate-500">소속일: {{ formatDate(currentAffiliation.approvedAt) }}</p>
                </div>
              </div>
            </div>

            <!-- 규칙 안내 -->
            <div class="bg-blue-50 border border-blue-200 rounded-2xl p-4">
              <div class="flex gap-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-[13px] font-bold text-blue-800 mb-2">소속 변경 규칙</p>
                  <ul class="text-[12px] text-blue-700 space-y-1">
                    <li>• 파트너는 <strong>1개의 현장에만 소속</strong></li>
                    <li>• 소속 변경은 <strong>30일마다 1회</strong></li>
                    <li>• 특별 사유 시 <strong>관리자 승인 후 즉시</strong></li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
              <div class="space-y-2">
                <router-link to="/partners/property/my" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">내 현장 정보</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link to="/partners/property/history" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">심사 결과 확인</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 우측: 현장 목록 -->
          <div class="lg:col-span-2">
            <!-- 로딩 -->
            <div v-if="loading" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
              <div class="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p class="text-[14px] text-slate-400">현장 목록을 불러오는 중...</p>
            </div>

            <!-- 대기중인 신청이 있는 경우 -->
            <div v-else-if="pendingApplication" class="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div class="flex items-center gap-4">
                <div class="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg class="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <h3 class="text-[15px] font-bold text-slate-900 mb-1">심사 대기중인 신청이 있습니다</h3>
                  <p class="text-[13px] text-slate-600">신청 현장: {{ pendingApplication.propertyName }}</p>
                  <p class="text-[12px] text-slate-400">신청일: {{ formatDate(pendingApplication.createdAt) }}</p>
                </div>
                <router-link to="/partners/property/history" class="flex items-center gap-1 px-4 py-2 bg-amber-500 text-white text-[13px] font-bold rounded-xl hover:bg-amber-600 transition">
                  심사 결과 확인
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>

            <!-- 현장 목록 -->
            <template v-else>
              <!-- 검색 -->
              <div class="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
                <div class="relative mb-2">
                  <svg class="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="현장명 또는 주소로 검색..."
                    class="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 text-[14px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
                <p class="text-[12px] text-slate-400">총 {{ filteredProperties.length }}개의 현장</p>
              </div>

              <!-- 현장 리스트 -->
              <div class="space-y-3">
                <div
                  v-for="property in filteredProperties"
                  :key="property.id"
                  :class="[
                    'bg-white border rounded-2xl p-4 flex items-center gap-4 transition-all',
                    currentAffiliation?.propertyId === property.id
                      ? 'border-emerald-300 bg-emerald-50/50'
                      : 'border-slate-200 hover:shadow-md'
                  ]"
                >
                  <div class="flex-shrink-0">
                    <span :class="[
                      'w-3 h-3 rounded-full block',
                      property.status === 'on-sale' ? 'bg-emerald-400 animate-pulse' :
                      property.status === 'pre-sale' ? 'bg-amber-400' : 'bg-slate-300'
                    ]"></span>
                  </div>

                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 class="text-[14px] font-bold text-slate-900">{{ property.name }}</h3>
                      <span :class="[
                        'px-2 py-0.5 rounded-full text-[11px] font-bold',
                        property.status === 'on-sale' ? 'bg-emerald-100 text-emerald-700' :
                        property.status === 'pre-sale' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      ]">
                        {{ getStatusLabel(property.status) }}
                      </span>
                      <span v-if="currentAffiliation?.propertyId === property.id" class="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[11px] font-bold">
                        현재 소속
                      </span>
                    </div>
                    <p class="flex items-center gap-1 text-[12px] text-slate-500">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      </svg>
                      {{ property.address }}
                    </p>
                  </div>

                  <div class="flex-shrink-0">
                    <span v-if="currentAffiliation?.propertyId === property.id" class="text-[13px] font-medium text-emerald-600">
                      현재 소속
                    </span>
                    <button
                      v-else
                      @click="openApplyModal(property)"
                      class="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-[13px] font-bold rounded-xl hover:bg-blue-700 transition"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                      </svg>
                      변경 신청
                    </button>
                  </div>
                </div>

                <!-- 빈 상태 -->
                <div v-if="filteredProperties.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
                  <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                    <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                  </div>
                  <p class="text-[15px] font-medium text-slate-500">검색 결과가 없습니다</p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 변경 신청 모달 -->
    <Teleport to="body">
      <div v-if="showApplyModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="closeApplyModal">
        <div class="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <div class="flex items-center justify-between p-5 border-b border-slate-100">
            <h3 class="text-[16px] font-bold text-slate-900">현장 변경 신청</h3>
            <button @click="closeApplyModal" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-5 overflow-y-auto flex-1">
            <!-- 변경 현장 정보 -->
            <div class="flex items-center justify-center gap-4 p-4 bg-slate-50 rounded-xl mb-4">
              <div v-if="currentAffiliation" class="text-center flex-1">
                <span class="block text-[11px] text-slate-500 mb-1">현재</span>
                <p class="text-[13px] font-bold text-slate-800">{{ currentAffiliation.propertyName }}</p>
              </div>
              <div v-if="currentAffiliation" class="flex-shrink-0">
                <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </div>
              <div class="text-center flex-1">
                <span class="block text-[11px] text-slate-500 mb-1">변경</span>
                <p class="text-[13px] font-bold text-blue-600">{{ selectedProperty?.name }}</p>
              </div>
            </div>

            <!-- 30일 제한 안내 -->
            <div v-if="currentAffiliation && !canChangeAffiliation" class="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl mb-4">
              <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>
                <p class="text-[13px] font-bold text-amber-800">30일 변경 제한 중입니다</p>
                <p class="text-[12px] text-amber-700">특별한 사유를 작성하시면 관리자 승인 후 즉시 변경됩니다.</p>
              </div>
            </div>

            <!-- 변경 사유 -->
            <div class="mb-4">
              <label class="block text-[13px] font-bold text-slate-700 mb-2">
                변경 사유
                <span v-if="!canChangeAffiliation" class="text-red-500">*</span>
                <span v-else class="text-slate-400 font-normal">(선택)</span>
              </label>
              <textarea
                v-model="changeReason"
                :placeholder="canChangeAffiliation ? '변경 사유를 입력해주세요 (선택사항)' : '특별한 사유를 상세히 작성해주세요 (필수)'"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[13px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                rows="3"
              ></textarea>
            </div>

            <!-- 명함 업로드 -->
            <div class="mb-4">
              <label class="block text-[13px] font-bold text-slate-700 mb-1">명함 인증 <span class="text-red-500">*</span></label>
              <p class="text-[11px] text-slate-400 mb-2">새로운 현장에서 활동 중임을 증명할 명함을 업로드해주세요</p>

              <div
                class="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition"
                @dragover.prevent
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  class="sr-only"
                  @change="handleFileSelect"
                />

                <div v-if="previewUrl" class="relative">
                  <img :src="previewUrl" alt="명함 미리보기" class="max-h-32 mx-auto rounded-lg" />
                  <button @click.stop="removeImage" class="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div v-else class="py-2">
                  <svg class="w-10 h-10 text-slate-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <p class="text-[12px] text-slate-500 mt-2">클릭하거나 드래그하세요</p>
                </div>
              </div>
            </div>

            <!-- 영업 정보 -->
            <div class="mb-4">
              <label class="block text-[13px] font-bold text-slate-700 mb-2">영업명 <span class="text-red-500">*</span></label>
              <input
                v-model="displayName"
                type="text"
                placeholder="고객에게 보여지는 이름"
                maxlength="10"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[13px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              />
              <p class="text-right text-[11px] text-slate-400 mt-1">{{ displayName.length }}/10</p>
            </div>

            <div class="mb-4">
              <label class="block text-[13px] font-bold text-slate-700 mb-2">한 줄 소개 <span class="text-red-500">*</span></label>
              <input
                v-model="introduction"
                type="text"
                placeholder="고객에게 보여지는 소개 문구"
                maxlength="20"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[13px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              />
              <p class="text-right text-[11px] text-slate-400 mt-1">{{ introduction.length }}/20</p>
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 p-5 border-t border-slate-100">
            <button @click="closeApplyModal" class="px-5 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition text-[13px] font-medium">
              취소
            </button>
            <button
              @click="submitApplication"
              :disabled="!canSubmit || submitting"
              class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-bold hover:bg-blue-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              <span v-if="submitting" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              {{ submitting ? '신청 중...' : '변경 신청하기' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, getDocs, addDoc, query, where, orderBy } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'ChangePropertyView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const { user, userProfile } = useAuth()

    const loading = ref(true)
    const properties = ref([])
    const searchQuery = ref('')
    const currentAffiliation = ref(null)
    const pendingApplication = ref(null)

    const showApplyModal = ref(false)
    const selectedProperty = ref(null)
    const changeReason = ref('')
    const displayName = ref('')
    const introduction = ref('')
    const businessCardFile = ref(null)
    const previewUrl = ref('')
    const submitting = ref(false)
    const fileInput = ref(null)

    const filteredProperties = computed(() => {
      if (!searchQuery.value) return properties.value
      const q = searchQuery.value.toLowerCase()
      return properties.value.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.address?.toLowerCase().includes(q)
      )
    })

    const canChangeAffiliation = computed(() => {
      if (!currentAffiliation.value?.approvedAt) return true
      const approved = new Date(currentAffiliation.value.approvedAt)
      const now = new Date()
      const diffDays = Math.floor((now - approved) / (1000 * 60 * 60 * 24))
      return diffDays >= 30
    })

    const daysUntilChange = computed(() => {
      if (!currentAffiliation.value?.approvedAt) return 0
      const approved = new Date(currentAffiliation.value.approvedAt)
      const now = new Date()
      const diffDays = Math.floor((now - approved) / (1000 * 60 * 60 * 24))
      return Math.max(0, 30 - diffDays)
    })

    const canSubmit = computed(() => {
      const hasRequiredFields = displayName.value.trim() && introduction.value.trim() && businessCardFile.value
      if (!canChangeAffiliation.value) {
        return hasRequiredFields && changeReason.value.trim()
      }
      return hasRequiredFields
    })

    const fetchData = async () => {
      if (!user.value) return

      try {
        const propertiesQuery = query(
          collection(db, 'properties'),
          orderBy('createdAt', 'desc')
        )
        const propertiesSnapshot = await getDocs(propertiesQuery)
        properties.value = propertiesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))

        const appsQuery = query(
          collection(db, 'partnerApplications'),
          where('partnerId', '==', user.value.uid)
        )
        const appsSnapshot = await getDocs(appsQuery)
        const apps = appsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))

        pendingApplication.value = apps.find(app => app.status === 'pending')
        currentAffiliation.value = apps.find(app => app.status === 'approved')

        if (currentAffiliation.value) {
          displayName.value = currentAffiliation.value.displayName || ''
          introduction.value = currentAffiliation.value.introduction || ''
        }
      } catch (error) {
        console.error('데이터 로드 실패:', error)
      }
    }

    const getStatusLabel = (status) => {
      const labels = {
        'pre-sale': '분양예정',
        'on-sale': '분양중',
        'completed': '분양완료'
      }
      return labels[status] || status
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    const openApplyModal = (property) => {
      selectedProperty.value = property
      showApplyModal.value = true
    }

    const closeApplyModal = () => {
      showApplyModal.value = false
      selectedProperty.value = null
      changeReason.value = ''
      businessCardFile.value = null
      previewUrl.value = ''
    }

    const triggerFileInput = () => {
      fileInput.value?.click()
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) processFile(file)
    }

    const handleDrop = (event) => {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        processFile(file)
      }
    }

    const processFile = (file) => {
      if (file.size > 5 * 1024 * 1024) {
        alert('파일 크기는 5MB 이하여야 합니다.')
        return
      }
      businessCardFile.value = file
      previewUrl.value = URL.createObjectURL(file)
    }

    const removeImage = () => {
      businessCardFile.value = null
      previewUrl.value = ''
      if (fileInput.value) fileInput.value.value = ''
    }

    const submitApplication = async () => {
      if (!canSubmit.value || submitting.value) return

      submitting.value = true

      try {
        const fileName = `business-cards/${user.value.uid}/${Date.now()}_${businessCardFile.value.name}`
        const imageRef = storageRef(storage, fileName)
        await uploadBytes(imageRef, businessCardFile.value)
        const businessCardUrl = await getDownloadURL(imageRef)

        await addDoc(collection(db, 'partnerApplications'), {
          partnerId: user.value.uid,
          partnerEmail: userProfile.value?.email || '',
          name: currentAffiliation.value?.name || userProfile.value?.name || '',
          phone: currentAffiliation.value?.phone || '',
          company: currentAffiliation.value?.company || '',
          position: currentAffiliation.value?.position || '',
          experience: currentAffiliation.value?.experience || '',
          displayName: displayName.value.trim(),
          introduction: introduction.value.trim(),
          businessCardUrl,
          propertyId: selectedProperty.value.id,
          propertyName: selectedProperty.value.name,
          changeReason: changeReason.value.trim(),
          isChange: true,
          previousPropertyId: currentAffiliation.value?.propertyId || null,
          previousPropertyName: currentAffiliation.value?.propertyName || null,
          requiresApproval: !canChangeAffiliation.value,
          status: 'pending',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })

        alert('변경 신청이 완료되었습니다.\n관리자 검토 후 승인 결과를 안내드리겠습니다.')
        closeApplyModal()
        await fetchData()
      } catch (error) {
        console.error('신청 실패:', error)
        alert('신청 중 오류가 발생했습니다.')
      } finally {
        submitting.value = false
      }
    }

    onMounted(async () => {
      loading.value = true
      await fetchData()
      loading.value = false
    })

    return {
      loading,
      properties,
      searchQuery,
      filteredProperties,
      currentAffiliation,
      pendingApplication,
      canChangeAffiliation,
      daysUntilChange,
      showApplyModal,
      selectedProperty,
      changeReason,
      displayName,
      introduction,
      previewUrl,
      submitting,
      fileInput,
      canSubmit,
      getStatusLabel,
      formatDate,
      openApplyModal,
      closeApplyModal,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      removeImage,
      submitApplication
    }
  }
}
</script>
